/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LiveAPIProvider } from './contexts/LiveAPIContext';
import { NetworkMonitor } from './lib/network-monitor';
import BlockingNotification from './components/common/BlockingNotification';
import { BalanceWarning } from './components/common/BalanceWarning';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import { Call, useUI } from './lib/state';

// Lazy load heavy components for code splitting
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const MyAgents = lazy(() => import('./components/myagents/MyAgents'));
const AgentConfig = lazy(() => import('./components/agentconfig/AgentConfig'));
const CreateAgent = lazy(() => import('./components/agentconfig/CreateAgent'));
const CallHistory = lazy(() => import('./components/callhistory/CallHistory'));
const CallTranscript = lazy(() => import('./components/callhistory/CallTranscript'));
const PaymentGateway = lazy(() => import('./components/paymentgateway/PaymentGateway'));
const Developer = lazy(() => import('./components/developer/Developer'));
const Contacts = lazy(() => import('./components/contacts/Contacts'));
const CRMDashboard = lazy(() => import('./components/crm/CRMDashboard'));
const Integrations = lazy(() => import('./components/integrations/Integrations'));
const PhoneCall = lazy(() => import('./components/phonecall/PhoneCall'));
const CallingDashboard = lazy(() => import('./components/phonecall/CallingDashboard'));
const AIChat = lazy(() => import('./components/aichat/AIChat'));
const ConversationHistory = lazy(() => import('./components/aichat/ConversationHistory'));
const AutomationCampaigns = lazy(() => import('./components/automation/AutomationCampaigns'));
const VoiceBot = lazy(() => import('./components/voicebot/VoiceBot').then(module => ({ default: module.VoiceBot })));
const ApifyLeadsPage = lazy(() => import('./components/apify/ApifyLeadsPage'));

// Loading component for Suspense fallback
const LoadingFallback: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '400px',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div className="loading-spinner" style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f4f6',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export type View =
  | 'Dashboard'
  | 'My Agents'
  | 'AI Chat'
  | 'Conversation History'
  | 'Phone Calls'
  | 'Agent Configuration'
  | 'Create Agent'
  | 'Call History'
  | 'Call Transcript'
  | 'Contacts'
  | 'Payment Gateway'
  | 'Developer'
  | 'CRM Dashboard'
  | 'Integrations'
  | 'Automation Campaigns';

// Main App Component with Authentication
function MainApp({ apiKey }: { apiKey: string }) {
  const [currentView, setCurrentView] = useState<View>('AI Chat');
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [showBlockingNotification, setShowBlockingNotification] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { isSidebarOpen, toggleSidebar } = useUI();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Global keyboard shortcut: Ctrl+Shift+Q to navigate to Developer
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Shift+Q (or Cmd+Shift+Q on Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Q') {
        event.preventDefault();
        // Navigate to Developer view
        setCurrentView('Developer');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Handle URL parameters for navigation
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam && viewParam !== currentView) {
      setCurrentView(viewParam as View);
    }
  }, [searchParams, currentView]);

  // Network monitoring for blocking detection
  useEffect(() => {
    const networkMonitor = NetworkMonitor.getInstance();
    
    const handleBlockingStatus = (blocked: boolean) => {
      setShowBlockingNotification(blocked);
    };

    networkMonitor.addListener(handleBlockingStatus);
    
    // Test connectivity on mount
    networkMonitor.testConnectivity().then(isConnected => {
      if (!isConnected) {
        setShowBlockingNotification(true);
      }
    });

    return () => {
      networkMonitor.removeListener(handleBlockingStatus);
    };
  }, []);

  const handleNavigate = useCallback((view: View, call?: Call) => {
    if (view === 'Call Transcript' && call) {
      setActiveCall(call);
    } else {
      setActiveCall(null);
    }
    setCurrentView(view);
  }, []);

  // Listen for navigation events from BalanceWarning
  useEffect(() => {
    const handleNavigateEvent = (event: CustomEvent) => {
      const view = event.detail?.view;
      if (view && typeof view === 'string') {
        handleNavigate(view as View);
      }
    };

    window.addEventListener('navigate', handleNavigateEvent as EventListener);
    return () => {
      window.removeEventListener('navigate', handleNavigateEvent as EventListener);
    };
  }, [handleNavigate]);

  const renderView = () => {
    switch (currentView) {
      case 'Dashboard':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CallingDashboard onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'My Agents':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <MyAgents onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'AI Chat':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <AIChat />
          </Suspense>
        );
      case 'Conversation History':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ConversationHistory />
          </Suspense>
        );
      case 'Phone Calls':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PhoneCall />
          </Suspense>
        );
      case 'Agent Configuration':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <AgentConfig onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Create Agent':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CreateAgent onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Call History':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CallHistory onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Call Transcript':
        return activeCall ? (
          <Suspense fallback={<LoadingFallback />}>
            <CallTranscript call={activeCall} onNavigate={handleNavigate} />
          </Suspense>
        ) : (
          <Suspense fallback={<LoadingFallback />}>
            <CallHistory onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Contacts':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Contacts onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Payment Gateway':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PaymentGateway />
          </Suspense>
        );
      case 'Developer':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Developer onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'CRM Dashboard':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CRMDashboard onNavigate={handleNavigate} />
          </Suspense>
        );
      case 'Integrations':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Integrations />
          </Suspense>
        );
      case 'Automation Campaigns':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <AutomationCampaigns />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CallingDashboard onNavigate={handleNavigate} />
          </Suspense>
        );
    }
  };

  return (
    <LiveAPIProvider apiKey={apiKey}>
      <div className={`app-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Mobile Overlay */}
          <div 
            className="sidebar-overlay"
            onClick={toggleSidebar}
          />
        
          <Sidebar 
            currentView={currentView} 
            onNavigate={(view) => {
              handleNavigate(view);
              // Close sidebar on mobile after navigation
              if (window.innerWidth <= 1024) {
                toggleSidebar();
              }
            }}
            onCollapseChange={setIsSidebarCollapsed}
          />
        <div className="main-content">
          <Header currentView={currentView} />
          <main className="content-area">
            <ErrorBoundary>
              {renderView()}
            </ErrorBoundary>
          </main>
        </div>
        
        {/* Network Blocking Notification */}
        {showBlockingNotification && (
          <BlockingNotification 
            onDismiss={() => setShowBlockingNotification(false)} 
          />
        )}

        {/* Balance Warning - Shows low balance and out of balance alerts */}
        {user && <BalanceWarning />}

        {/* Voice Bot */}
        {user && (
          <Suspense fallback={null}>
          <VoiceBot 
            currentView={currentView}
            onNavigate={handleNavigate}
            apiKey={apiKey}
          />
          </Suspense>
        )}
      </div>
    </LiveAPIProvider>
  );
}

// Component that handles all auth-required routes
function AuthRequiredRoutes({ apiKey }: { apiKey: string }) {
  return (
    <AuthProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        
        {/* Protected Routes - Direct to Main App */}
        <Route path="/app/*" element={
          <ProtectedRoute>
            <ErrorBoundary>
              <MainApp apiKey={apiKey} />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AuthProvider>
  );
}

// Root App Component with Router and Auth
export default function App() {
  const apiKey = (import.meta as any).env?.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY;

  if (!apiKey) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif' }}>
        <h1>API Key Not Found</h1>
        <p>Please ensure the <code>VITE_GEMINI_API_KEY</code> environment variable is set.</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Standalone Public Leads Page - Completely isolated, NO AuthProvider */}
          <Route path="/leads" element={
            <Suspense fallback={<LoadingFallback />}>
              <ApifyLeadsPage />
            </Suspense>
          } />
          
          {/* All other routes (including catch-all) go through AuthProvider */}
          <Route path="*" element={<AuthRequiredRoutes apiKey={apiKey} />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}